import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  User,
  Phone,
  MapPin,
  Euro,
  Mail,
  Lock,
  Globe,
  FileText,
  ChevronRight,
  Loader2,
  Check,
  ArrowLeft,
  Link2,
  CreditCard,
  Landmark
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import LegalAcceptanceForm from "../components/LegalAcceptanceForm";
import { isLegalAcceptanceComplete } from "../config/legalVersions";
import { TIPI_STRUTTURA, coerceTipoStruttura } from "../config/tipiStruttura";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { id: "tier_1", label: "1 Struttura", strutture: 1, defaultPrice: 79, defaultPriceYearly: 790 },
  { id: "tier_2", label: "2 Strutture", strutture: 2, defaultPrice: 139, defaultPriceYearly: 1390 },
  { id: "tier_3", label: "3 Strutture", strutture: 3, defaultPrice: 189, defaultPriceYearly: 1890 },
  { id: "tier_4", label: "4 Strutture", strutture: 4, defaultPrice: 229, defaultPriceYearly: 2290 },
  { id: "tier_5", label: "5 Strutture", strutture: 5, defaultPrice: 259, defaultPriceYearly: 2590 },
  { id: "tier_6", label: "6 Strutture", strutture: 6, defaultPrice: 289, defaultPriceYearly: 2890 },
  { id: "tier_7", label: "7 Strutture", strutture: 7, defaultPrice: 319, defaultPriceYearly: 3190 },
  { id: "tier_8", label: "8 Strutture", strutture: 8, defaultPrice: 349, defaultPriceYearly: 3490 },
  { id: "tier_9", label: "9 Strutture", strutture: 9, defaultPrice: 379, defaultPriceYearly: 3790 },
  { id: "tier_10", label: "10 Strutture", strutture: 10, defaultPrice: 399, defaultPriceYearly: 3990 },
  { id: "tier_unlimited", label: "10+ Strutture", strutture: 999, defaultPrice: 499, defaultPriceYearly: 4990 },
];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingZoho, setLoadingZoho] = useState(false);
  const [zohoLoaded, setZohoLoaded] = useState(false);
  const [hasYearlyPlan, setHasYearlyPlan] = useState(false);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [step, setStep] = useState(1); // 1: Struttura, 2: Referente/Login
  const [showMultiStrutturaModal, setShowMultiStrutturaModal] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Struttura - Obbligatori
    organizationName: "",
    tipoStruttura: "",
    postiTotali: "",
    telefonoStruttura: "",
    comune: "",
    via: "",
    numeroCivico: "",
    provincia: "",
    tariffaDoppiaWc: "",
    tariffaSingolaWc: "",
    // Struttura - Facoltativi
    sitoWeb: "",
    partitaIva: "",
    pec: "",
    sdi: "",
    zohoRecordId: "", // ID struttura esistente su Zoho (opzionale)
    // Referente
    nomeReferente: "",
    telefonoReferente: "",
    // Login
    adminEmail: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    // Piano
    plan: "monthly",
    plan_tier: "tier_1",
    // Metodo di pagamento
    paymentMethod: "stripe",
    // Accettazione legale
    acceptedTerms: false,
    acceptedClauses1341: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const checkPlans = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/billing/plans`);
        setHasYearlyPlan(response.data.hasYearly);
        if (response.data.tiers && response.data.tiers.length > 0) {
          setAvailableTiers(response.data.tiers);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };
    checkPlans();
  }, []);

  const invitoFarmacia = searchParams.get("invito_farmacia") || "";
  const invitoCommerciale = searchParams.get("invito_commerciale") || "";
  const invitoToken = invitoCommerciale || invitoFarmacia;
  const invitoLocked = Boolean(invitoToken);
  useEffect(() => {
    if (!invitoToken) return;
    const url = invitoCommerciale
      ? `${API_URL}/api/commerciale/onboarding/invito/${encodeURIComponent(invitoCommerciale)}`
      : `${API_URL}/api/farmacia/strutture/invito/${encodeURIComponent(invitoFarmacia)}`;
    axios.get(url)
      .then((res) => {
        if (!res.data?.valid) {
          toast.error("Invito non valido o scaduto");
          return;
        }
        const prefill = res.data.prefill || {};
        setFormData((prev) => ({
          ...prev,
          organizationName: prefill.nome || prev.organizationName,
          tipoStruttura: coerceTipoStruttura(prefill.tipo_struttura) || prev.tipoStruttura,
          postiTotali: prefill.posti_totali || prev.postiTotali,
          telefonoStruttura: prefill.telefono || prev.telefonoStruttura,
          comune: prefill.comune || prev.comune,
          via: prefill.via || prev.via,
          numeroCivico: prefill.numero_civico || prev.numeroCivico,
          provincia: prefill.provincia || prev.provincia,
          partitaIva: prefill.partita_iva || prev.partitaIva,
          pec: prefill.pec || prev.pec,
          sdi: prefill.sdi || prev.sdi,
          nomeReferente: prefill.referente_nome || prev.nomeReferente,
          adminEmail: res.data.email || prev.adminEmail,
          confirmEmail: res.data.email || prev.confirmEmail,
        }));
      })
      .catch(() => toast.error("Invito non valido o scaduto"));
  }, [invitoToken, invitoCommerciale, invitoFarmacia]);

  // Fetch data from Zoho when ID is entered
  const fetchZohoData = async (zohoId) => {
    if (!zohoId || zohoId.length < 10) return;
    
    setLoadingZoho(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/zoho-struttura/${zohoId}`);
      
      if (response.data.success && response.data.data) {
        const zohoData = response.data.data;
        
        // Update form with Zoho data, keeping existing values for empty fields
        setFormData(prev => ({
          ...prev,
          organizationName: zohoData.organizationName || prev.organizationName,
          tipoStruttura: zohoData.tipoStruttura || prev.tipoStruttura,
          postiTotali: zohoData.postiTotali || prev.postiTotali,
          telefonoStruttura: zohoData.telefonoStruttura || prev.telefonoStruttura,
          comune: zohoData.comune || prev.comune,
          via: zohoData.via || prev.via,
          numeroCivico: zohoData.numeroCivico || prev.numeroCivico,
          provincia: zohoData.provincia || prev.provincia,
          tariffaDoppiaWc: zohoData.tariffaDoppiaWc || prev.tariffaDoppiaWc,
          tariffaSingolaWc: zohoData.tariffaSingolaWc || prev.tariffaSingolaWc,
          sitoWeb: zohoData.sitoWeb || prev.sitoWeb,
          partitaIva: zohoData.partitaIva || prev.partitaIva,
          pec: zohoData.pec || prev.pec,
          sdi: zohoData.sdi || prev.sdi,
          nomeReferente: zohoData.nomeReferente || prev.nomeReferente,
          telefonoReferente: zohoData.telefonoReferente || prev.telefonoReferente,
        }));
        
        setZohoLoaded(true);
        toast.success("Dati struttura caricati! Completa i campi mancanti.");
      }
    } catch (error) {
      console.error("Error fetching Zoho data:", error);
      // Mostra messaggio di errore più specifico
      let message = "Impossibile recuperare i dati della struttura";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.message === "Network Error") {
        message = "Errore di rete. Verifica la connessione.";
      }
      toast.error(message);
      setZohoLoaded(false);
    } finally {
      setLoadingZoho(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle Zoho ID change with debounce
  const handleZohoIdChange = (value) => {
    setFormData(prev => ({ ...prev, zohoRecordId: value }));
    setZohoLoaded(false);
    
    // Clear timeout if exists
    if (window.zohoIdTimeout) {
      clearTimeout(window.zohoIdTimeout);
    }
    
    // Debounce the API call
    if (value && value.length >= 15) {
      window.zohoIdTimeout = setTimeout(() => {
        fetchZohoData(value);
      }, 800);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.organizationName.trim()) newErrors.organizationName = "Nome struttura obbligatorio";
    if (!formData.tipoStruttura) newErrors.tipoStruttura = "Seleziona il tipo struttura";
    if (!formData.postiTotali || parseInt(formData.postiTotali) <= 0) newErrors.postiTotali = "Posti totali obbligatorio";
    if (!formData.telefonoStruttura.trim()) newErrors.telefonoStruttura = "Telefono obbligatorio";
    if (!formData.comune.trim()) newErrors.comune = "Comune obbligatorio";
    if (!formData.via.trim()) newErrors.via = "Via obbligatoria";
    if (!formData.numeroCivico.trim()) newErrors.numeroCivico = "Numero civico obbligatorio";
    if (!formData.provincia.trim()) newErrors.provincia = "Provincia obbligatoria";
    const tariffaDoppia = parseFloat(formData.tariffaDoppiaWc);
    const tariffaSingola = parseFloat(formData.tariffaSingolaWc);
    if (formData.tariffaDoppiaWc === "" || isNaN(tariffaDoppia) || tariffaDoppia < 0) newErrors.tariffaDoppiaWc = "Inserisci un importo ≥ 0 (0 se non avete camere doppie)";
    if (formData.tariffaSingolaWc === "" || isNaN(tariffaSingola) || tariffaSingola < 0) newErrors.tariffaSingolaWc = "Inserisci un importo ≥ 0 (0 se non avete camere singole)";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.nomeReferente.trim()) newErrors.nomeReferente = "Nome referente obbligatorio";
    if (!formData.telefonoReferente.trim()) newErrors.telefonoReferente = "Telefono referente obbligatorio";
    if (!formData.adminEmail.trim()) newErrors.adminEmail = "Email obbligatoria";
    else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) newErrors.adminEmail = "Email non valida";
    if (formData.adminEmail !== formData.confirmEmail) newErrors.confirmEmail = "Le email non coincidono";
    if (!formData.password) newErrors.password = "Password obbligatoria";
    else if (formData.password.length < 10) newErrors.password = "Password minimo 10 caratteri con maiuscole, minuscole, numeri e caratteri speciali";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Le password non coincidono";
    if (!formData.acceptedTerms) newErrors.acceptedTerms = "Devi accettare i Termini di Utilizzo per procedere";
    if (!formData.acceptedClauses1341) newErrors.acceptedClauses1341 = "Devi approvare le clausole ex artt. 1341-1342 c.c.";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setLoading(true);
    
    try {
      const payload = {
        organizationName: formData.organizationName,
        tipoStruttura: formData.tipoStruttura,
        postiTotali: parseInt(formData.postiTotali),
        telefonoStruttura: formData.telefonoStruttura,
        comune: formData.comune,
        via: formData.via,
        numeroCivico: formData.numeroCivico,
        provincia: formData.provincia,
        tariffaDoppiaWc: parseFloat(formData.tariffaDoppiaWc),
        tariffaSingolaWc: parseFloat(formData.tariffaSingolaWc),
        sitoWeb: formData.sitoWeb || null,
        partitaIva: formData.partitaIva || null,
        pec: formData.pec || null,
        sdi: formData.sdi || null,
        zohoRecordId: formData.zohoRecordId || null,
        nomeReferente: formData.nomeReferente,
        telefonoReferente: formData.telefonoReferente,
        adminEmail: formData.adminEmail,
        password: formData.password,
        plan: formData.plan,
        plan_tier: formData.plan_tier,
        paymentMethod: formData.paymentMethod,
        acceptedTerms: formData.acceptedTerms,
        acceptedClauses1341: formData.acceptedClauses1341,
        farmacia_invito_token: invitoFarmacia || null,
        commerciale_invito_token: invitoCommerciale || null,
      };

      const response = await axios.post(
        `${API_URL}/api/auth/register-and-checkout`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Origin": window.location.origin
          }
        }
      );

      if (response.data.success) {
        if (response.data.requiresStripeRedirect === false) {
          // Bonifico bancario: vai alla pagina di conferma interna
          navigate(`/register/bonifico-success?org=${response.data.organizationId}`);
        } else if (response.data.checkoutUrl) {
          toast.success("Reindirizzamento al pagamento...");
          window.location.href = response.data.checkoutUrl;
        } else {
          throw new Error("Risposta non valida dal server");
        }
      } else {
        throw new Error("Risposta non valida dal server");
      }
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || "Errore durante la registrazione";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#f7faf9] px-4 py-6 text-slate-900">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 right-[-10rem] h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute bottom-[-12rem] left-[-8rem] h-[28rem] w-[28rem] rounded-full bg-teal-100/70 blur-3xl" />
      </div>

      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-col gap-5 rounded-[2rem] border border-white/80 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Link to="/login" className="mb-4 inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-emerald-200 hover:text-emerald-700">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
            Torna al login
          </Link>
            <div className="flex items-center gap-4">
              <img
                src="/images/logo-full.png"
                alt="Affidya"
                className="h-12 object-contain"
              />
              <span className="hidden h-9 w-px bg-slate-200 sm:block" />
              <span className="hidden rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 sm:inline-flex">
                Onboarding struttura
              </span>
            </div>
            <div className="mt-5">
              <h1 className="text-3xl font-bold tracking-tight text-slate-950">Attiva Affidya per la tua struttura</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                {invitoCommerciale
                  ? "Sei stato invitato dal team Affidya. Completa i dati, crea le credenziali e scegli piano e pagamento per attivare l'account."
                  : invitoFarmacia
                  ? "Sei stato invitato dalla farmacia partner. Completa i dati, scegli il piano e il pagamento: dopo l'attivazione la struttura sarà collegata alla farmacia."
                  : "Completa i dati essenziali, crea l'account amministratore e scegli il piano più adatto. Il checkout sicuro parte al termine della registrazione."}
              </p>
            </div>
          </div>

          <div className="grid gap-3 rounded-2xl bg-slate-950 p-4 text-white shadow-xl shadow-slate-200/70 sm:min-w-[260px]">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300">
                <Check className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">Setup guidato</p>
                <p className="text-xs text-slate-300">2 step, pagamento protetto</p>
              </div>
            </div>
            <div className="h-px bg-white/10" />
            <p className="text-xs leading-5 text-slate-300">
              I campi obbligatori sono evidenziati. Potrai completare configurazioni avanzate dopo il primo accesso.
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-6 flex items-center justify-center gap-3">
          <div className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${step >= 1 ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-slate-400'}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${step >= 1 ? 'bg-emerald-600 text-white' : 'bg-slate-200'}`}>
              {step > 1 ? <Check className="w-4 h-4" /> : "1"}
            </div>
            <span>Struttura</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300" />
          <div className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${step >= 2 ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-slate-400'}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${step >= 2 ? 'bg-emerald-600 text-white' : 'bg-slate-200'}`}>
              2
            </div>
            <span>Referente & Account</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Struttura */}
          {step === 1 && (
            <Card className="overflow-hidden rounded-[2rem] border border-white/80 bg-white/95 shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
              <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-white via-emerald-50/60 to-white px-6 py-5">
                <CardTitle className="flex items-center gap-3 text-xl text-slate-950">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <Building2 className="h-5 w-5" />
                  </span>
                  Dati struttura
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-6">
                {/* Sei già una nostra struttura? - IN ALTO */}
                <div className={`rounded-2xl border p-4 ${zohoLoaded ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <Label htmlFor="zohoRecordId" className={`mb-2 flex items-center gap-2 font-semibold ${zohoLoaded ? 'text-emerald-800' : 'text-slate-800'}`}>
                    <Link2 className="h-4 w-4" /> Hai già un ID struttura (legacy) da collegare?
                  </Label>
                  <p className={`mb-3 text-sm ${zohoLoaded ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {zohoLoaded 
                      ? "Dati caricati. Verifica e completa i campi mancanti sotto."
                      : "Inserisci l'ID struttura (legacy) per precompilare i dati disponibili. Se non lo hai, lascia il campo vuoto e crea una nuova struttura."
                    }
                  </p>
                  <div className="relative">
                    <Input
                      id="zohoRecordId"
                      value={formData.zohoRecordId}
                      onChange={(e) => handleZohoIdChange(e.target.value)}
                      placeholder="Es. 876565000000566102"
                      className={`h-11 rounded-xl bg-white pr-10 ${zohoLoaded ? 'border-emerald-400' : ''}`}
                    />
                    {loadingZoho && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                      </div>
                    )}
                    {zohoLoaded && !loadingZoho && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check className="h-5 w-5 text-emerald-600" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Nome e Tipo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="organizationName" className="flex items-center gap-1">
                      Nome Struttura <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="organizationName"
                      value={formData.organizationName}
                      onChange={(e) => handleChange("organizationName", e.target.value)}
                      placeholder="Es. Residenza Villa Serena"
                      className={errors.organizationName ? "border-red-500" : ""}
                    />
                    {errors.organizationName && <p className="text-red-500 text-sm mt-1">{errors.organizationName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="tipoStruttura" className="flex items-center gap-1">
                      Tipo Struttura <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.tipoStruttura} onValueChange={(v) => handleChange("tipoStruttura", v)}>
                      <SelectTrigger className={errors.tipoStruttura ? "border-red-500" : ""}>
                        <SelectValue placeholder="Seleziona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI_STRUTTURA.map(tipo => (
                          <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.tipoStruttura && <p className="text-red-500 text-sm mt-1">{errors.tipoStruttura}</p>}
                  </div>
                </div>

                {/* Posti e Telefono */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postiTotali" className="flex items-center gap-1">
                      Posti Totali <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postiTotali"
                      type="number"
                      min="1"
                      value={formData.postiTotali}
                      onChange={(e) => handleChange("postiTotali", e.target.value)}
                      placeholder="Es. 50"
                      className={errors.postiTotali ? "border-red-500" : ""}
                    />
                    {errors.postiTotali && <p className="text-red-500 text-sm mt-1">{errors.postiTotali}</p>}
                  </div>
                  <div>
                    <Label htmlFor="telefonoStruttura" className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Telefono Struttura <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="telefonoStruttura"
                      value={formData.telefonoStruttura}
                      onChange={(e) => handleChange("telefonoStruttura", e.target.value)}
                      placeholder="+39 02 1234567"
                      className={errors.telefonoStruttura ? "border-red-500" : ""}
                    />
                    {errors.telefonoStruttura && <p className="text-red-500 text-sm mt-1">{errors.telefonoStruttura}</p>}
                  </div>
                </div>

                {/* Indirizzo */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Indirizzo
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="via">Via <span className="text-red-500">*</span></Label>
                      <Input
                        id="via"
                        value={formData.via}
                        onChange={(e) => handleChange("via", e.target.value)}
                        placeholder="Via Roma"
                        className={errors.via ? "border-red-500" : ""}
                      />
                      {errors.via && <p className="text-red-500 text-sm mt-1">{errors.via}</p>}
                    </div>
                    <div>
                      <Label htmlFor="numeroCivico">N. Civico <span className="text-red-500">*</span></Label>
                      <Input
                        id="numeroCivico"
                        value={formData.numeroCivico}
                        onChange={(e) => handleChange("numeroCivico", e.target.value)}
                        placeholder="123"
                        className={errors.numeroCivico ? "border-red-500" : ""}
                      />
                      {errors.numeroCivico && <p className="text-red-500 text-sm mt-1">{errors.numeroCivico}</p>}
                    </div>
                    <div>
                      <Label htmlFor="provincia">Provincia <span className="text-red-500">*</span></Label>
                      <Input
                        id="provincia"
                        value={formData.provincia}
                        onChange={(e) => handleChange("provincia", e.target.value)}
                        placeholder="MI"
                        maxLength={2}
                        className={errors.provincia ? "border-red-500" : ""}
                      />
                      {errors.provincia && <p className="text-red-500 text-sm mt-1">{errors.provincia}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="comune">Comune <span className="text-red-500">*</span></Label>
                    <Input
                      id="comune"
                      value={formData.comune}
                      onChange={(e) => handleChange("comune", e.target.value)}
                      placeholder="Milano"
                      className={errors.comune ? "border-red-500" : ""}
                    />
                    {errors.comune && <p className="text-red-500 text-sm mt-1">{errors.comune}</p>}
                  </div>
                </div>

                {/* Tariffe */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Euro className="w-4 h-4" /> Tariffe Mensili
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tariffaDoppiaWc">Camera doppia a partire da <span className="text-red-500">*</span></Label>
                      <Input
                        id="tariffaDoppiaWc"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.tariffaDoppiaWc}
                        onChange={(e) => handleChange("tariffaDoppiaWc", e.target.value)}
                        placeholder="1.800"
                        className={errors.tariffaDoppiaWc ? "border-red-500" : ""}
                      />
                      {errors.tariffaDoppiaWc && <p className="text-red-500 text-sm mt-1">{errors.tariffaDoppiaWc}</p>}
                    </div>
                    <div>
                      <Label htmlFor="tariffaSingolaWc">Camera singola a partire da <span className="text-red-500">*</span></Label>
                      <Input
                        id="tariffaSingolaWc"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.tariffaSingolaWc}
                        onChange={(e) => handleChange("tariffaSingolaWc", e.target.value)}
                        placeholder="2.000"
                        className={errors.tariffaSingolaWc ? "border-red-500" : ""}
                      />
                      {errors.tariffaSingolaWc && <p className="text-red-500 text-sm mt-1">{errors.tariffaSingolaWc}</p>}
                    </div>
                  </div>
                </div>

                {/* Dati Facoltativi */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sitoWeb" className="flex items-center gap-1">
                        <Globe className="w-4 h-4" /> Sito Web
                      </Label>
                      <Input
                        id="sitoWeb"
                        value={formData.sitoWeb}
                        onChange={(e) => handleChange("sitoWeb", e.target.value)}
                        placeholder="www.esempio.it"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partitaIva">Partita IVA</Label>
                      <Input
                        id="partitaIva"
                        value={formData.partitaIva}
                        onChange={(e) => handleChange("partitaIva", e.target.value)}
                        placeholder="IT12345678901"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pec">PEC</Label>
                      <Input
                        id="pec"
                        type="email"
                        value={formData.pec}
                        onChange={(e) => handleChange("pec", e.target.value)}
                        placeholder="struttura@pec.it"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sdi">Codice SDI</Label>
                      <Input
                        id="sdi"
                        value={formData.sdi}
                        onChange={(e) => handleChange("sdi", e.target.value)}
                        placeholder="XXXXXXX"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="button" 
                  onClick={handleNextStep}
                  className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
                >
                  Continua <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Referente & Account */}
          {step === 2 && (
            <Card className="overflow-hidden rounded-[2rem] border border-white/80 bg-white/95 shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
              <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-white via-emerald-50/60 to-white px-6 py-5">
                <CardTitle className="flex items-center gap-3 text-xl text-slate-950">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <User className="h-5 w-5" />
                  </span>
                  Referente & Account
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 p-6">
                {/* Referente */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700">Dati Referente</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nomeReferente" className="flex items-center gap-1">
                        Nome Referente <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nomeReferente"
                        value={formData.nomeReferente}
                        onChange={(e) => handleChange("nomeReferente", e.target.value)}
                        placeholder="Mario Rossi"
                        className={errors.nomeReferente ? "border-red-500" : ""}
                      />
                      {errors.nomeReferente && <p className="text-red-500 text-sm mt-1">{errors.nomeReferente}</p>}
                    </div>
                    <div>
                      <Label htmlFor="telefonoReferente" className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> Telefono Referente <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="telefonoReferente"
                        value={formData.telefonoReferente}
                        onChange={(e) => handleChange("telefonoReferente", e.target.value)}
                        placeholder="+39 333 1234567"
                        className={errors.telefonoReferente ? "border-red-500" : ""}
                      />
                      {errors.telefonoReferente && <p className="text-red-500 text-sm mt-1">{errors.telefonoReferente}</p>}
                    </div>
                  </div>
                </div>

                {/* Credenziali */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Credenziali di Accesso
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="adminEmail" className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        value={formData.adminEmail}
                        onChange={(e) => handleChange("adminEmail", e.target.value)}
                        placeholder="admin@struttura.it"
                        readOnly={invitoLocked}
                        className={errors.adminEmail ? "border-red-500" : ""}
                      />
                      {errors.adminEmail && <p className="text-red-500 text-sm mt-1">{errors.adminEmail}</p>}
                    </div>
                    <div>
                      <Label htmlFor="confirmEmail">Conferma Email <span className="text-red-500">*</span></Label>
                      <Input
                        id="confirmEmail"
                        type="email"
                        value={formData.confirmEmail}
                        onChange={(e) => handleChange("confirmEmail", e.target.value)}
                        placeholder="Ripeti l'email"
                        readOnly={invitoLocked}
                        className={errors.confirmEmail ? "border-red-500" : ""}
                      />
                      {errors.confirmEmail && <p className="text-red-500 text-sm mt-1">{errors.confirmEmail}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder="Minimo 6 caratteri"
                        className={errors.password ? "border-red-500" : ""}
                      />
                      {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword">Conferma Password <span className="text-red-500">*</span></Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        placeholder="Ripeti la password"
                        className={errors.confirmPassword ? "border-red-500" : ""}
                      />
                      {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                </div>

                {/* Numero Strutture */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700">Numero di Strutture</h3>
                  {formData.plan_tier === "tier_1" ? (
                    <>
                      {(() => {
                        const tier1 = TIER_OPTIONS[0];
                        const apiTier = availableTiers.find(at => at.id === tier1.id);
                        const monthlyPrice = apiTier?.price_monthly ?? tier1.defaultPrice;
                        return (
                          <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                            <div className="text-2xl font-bold text-slate-900">1</div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">Struttura singola</p>
                              <p className="text-xs font-semibold text-emerald-700">€{monthlyPrice}/mese</p>
                            </div>
                            <Check className="h-5 w-5 shrink-0 text-emerald-600" />
                          </div>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={() => setShowMultiStrutturaModal(true)}
                        className="text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                      >
                        Gestisci più di una struttura?
                      </button>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const selectedTier = TIER_OPTIONS.find(t => t.id === formData.plan_tier);
                        const apiTier = availableTiers.find(at => at.id === formData.plan_tier);
                        const monthlyPrice = apiTier?.price_monthly ?? selectedTier?.defaultPrice;
                        return (
                          <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                            <div className="text-2xl font-bold text-slate-900">{selectedTier?.strutture === 999 ? "10+" : selectedTier?.strutture}</div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">{selectedTier?.label}</p>
                              <p className="text-xs font-semibold text-emerald-700">€{monthlyPrice}/mese</p>
                            </div>
                            <Check className="h-5 w-5 shrink-0 text-emerald-600" />
                          </div>
                        );
                      })()}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowMultiStrutturaModal(true)}
                          className="text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                        >
                          Cambia numero strutture
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          onClick={() => handleChange("plan_tier", "tier_1")}
                          className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
                        >
                          Torna a 1 struttura
                        </button>
                      </div>
                      <p className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-700">
                        Potrai aggiungere le altre strutture dopo la registrazione, nella sezione Impostazioni.
                      </p>
                    </>
                  )}
                </div>

                {/* Dialog selezione multi-struttura */}
                <Dialog open={showMultiStrutturaModal} onOpenChange={setShowMultiStrutturaModal}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Quante strutture vuoi gestire?</DialogTitle>
                      <DialogDescription>Seleziona il numero di strutture da gestire con Affidya.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 py-2">
                      {TIER_OPTIONS.filter(t => t.id !== "tier_1").map((tier) => {
                        const apiTier = availableTiers.find(at => at.id === tier.id);
                        const monthlyPrice = apiTier?.price_monthly ?? tier.defaultPrice;
                        const yearlyPrice = apiTier?.price_yearly;
                        const displayPrice = formData.plan === 'yearly' && yearlyPrice
                          ? `€${yearlyPrice}/anno`
                          : monthlyPrice ? `€${monthlyPrice}/mese` : null;
                        return (
                          <div
                            key={tier.id}
                            className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                              formData.plan_tier === tier.id
                                ? 'border-emerald-500 bg-emerald-50 shadow-md'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => {
                              handleChange("plan_tier", tier.id);
                              setShowMultiStrutturaModal(false);
                            }}
                          >
                            <div className="text-2xl font-bold text-slate-900">{tier.strutture === 999 ? "10+" : tier.strutture}</div>
                            <p className="text-xs text-slate-600 mt-1">strutture</p>
                            {displayPrice && (
                              <p className={`text-xs font-semibold mt-1 ${
                                formData.plan_tier === tier.id ? 'text-emerald-700' : 'text-slate-500'
                              }`}>{displayPrice}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Piano */}
                {(() => {
                  const selectedTierOption = TIER_OPTIONS.find(t => t.id === formData.plan_tier) || TIER_OPTIONS[0];
                  const apiTier = availableTiers.find(at => at.id === formData.plan_tier);
                  const currentMonthly = apiTier?.price_monthly ?? selectedTierOption.defaultPrice;
                  const currentYearly = apiTier?.price_yearly ?? selectedTierOption.defaultPriceYearly ?? (currentMonthly * 10);
                  return (
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="font-semibold text-slate-700">Piano Abbonamento</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div 
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.plan === 'monthly' 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleChange("plan", "monthly")}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-slate-900">Mensile</p>
                              <p className="text-2xl font-bold text-emerald-700">€{currentMonthly}<span className="text-sm font-normal text-slate-500">/mese</span></p>
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              formData.plan === 'monthly' ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                            }`}>
                              {formData.plan === 'monthly' && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                        </div>
                        
                        {hasYearlyPlan && (
                          <div 
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              formData.plan === 'yearly' 
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => handleChange("plan", "yearly")}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-slate-900">Annuale</p>
                                <p className="text-2xl font-bold text-emerald-700">€{currentYearly}<span className="text-sm font-normal text-slate-500">/anno</span></p>
                                <p className="text-xs text-emerald-600">Risparmia 2 mesi!</p>
                              </div>
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                formData.plan === 'yearly' ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                              }`}>
                                {formData.plan === 'yearly' && <Check className="w-4 h-4 text-white" />}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Metodo di Pagamento */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700">Metodo di Pagamento</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.paymentMethod === "stripe"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => handleChange("paymentMethod", "stripe")}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                          formData.paymentMethod === "stripe" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}>
                          <CreditCard className="h-5 w-5" />
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">Carta di credito</p>
                          <p className="text-xs text-slate-500">Pagamento sicuro via Stripe</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          formData.paymentMethod === "stripe" ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
                        }`}>
                          {formData.paymentMethod === "stripe" && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.paymentMethod === "bank_transfer"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => handleChange("paymentMethod", "bank_transfer")}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                          formData.paymentMethod === "bank_transfer" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}>
                          <Landmark className="h-5 w-5" />
                        </span>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">Bonifico bancario</p>
                          <p className="text-xs text-slate-500">Attivazione dopo verifica accredito</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          formData.paymentMethod === "bank_transfer" ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
                        }`}>
                          {formData.paymentMethod === "bank_transfer" && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </div>
                  </div>
                  {formData.paymentMethod === "bank_transfer" && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                      <strong>Nota:</strong> La registrazione andrà a buon fine ma l'accesso ad Affidya sarà disponibile
                      solo dopo che verificheremo l'accredito del bonifico (di solito entro 1–2 giorni lavorativi).
                      Riceverai via email le coordinate bancarie e le istruzioni per il pagamento.
                    </div>
                  )}
                </div>

                {/* Accettazione legale */}
                <LegalAcceptanceForm
                  values={{
                    acceptedTerms: formData.acceptedTerms,
                    acceptedClauses1341: formData.acceptedClauses1341,
                  }}
                  onChange={handleChange}
                  errors={errors}
                  compact
                />

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="h-12 flex-1 rounded-xl"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading || !isLegalAcceptanceComplete(formData)}
                    className="h-12 flex-1 rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Elaborazione...
                      </>
                    ) : formData.paymentMethod === "bank_transfer" ? (
                      <>
                        <Landmark className="w-5 h-5 mr-2" />
                        Completa registrazione
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Procedi al pagamento
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm mt-8 space-y-2">
          <p>© {new Date().getFullYear()} Affidya S.r.l. - Tutti i diritti riservati</p>
          <div className="flex justify-center gap-4">
            <Link to="/legal/privacy" className="hover:text-slate-700">Privacy Policy</Link>
            <Link to="/legal/cookies" className="hover:text-slate-700">Cookie Policy</Link>
            <Link to="/legal/terms" className="hover:text-slate-700">Termini di Servizio</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
